import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { unformatCnpj } from '../utils/cnpj.util';

export interface CnpjOfficeData {
  company?: {
    name?: string;
  };
  alias?: string;
  responsibleName?: string;
  mainActivityText?: string;
  address?: {
    street?: string;
    number?: string;
    details?: string;
    district?: string;
    zip?: string;
    state?: string;
    city?: string;
  };
  /** Somente dígitos, 10 ou 11 caracteres */
  phoneDigits?: string;
  email?: string;
  /**
   * Texto para a etapa de produto (ex.: descrição da atividade/CNAE), quando a API informar.
   */
  suggestedProductDescription?: string;
  /**
   * Não vem das APIs; usado ao reabrir o formulário após a tela "Comece aqui" para repovoar o campo CNPJ.
   */
  formattedCnpjForForm?: string;
}

interface BrasilApiCnpjResponse {
  razao_social?: string;
  nome_fantasia?: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
  email?: string | null;
  ddd_telefone_1?: string;
  telefone_1?: string;
  ddd_telefone_2?: string;
  telefone_2?: string;
  cnae_fiscal_descricao?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CnpjLookupService {
  private http = inject(HttpClient);


  /**
   * Consulta primeiro a CNPJa (open.cnpja.com). A Brasil API só é chamada se essa requisição falhar.
   */
  fetchOffice(cnpj: string): Observable<CnpjOfficeData> {
    const cleanCnpj = unformatCnpj(cnpj);
    if (cleanCnpj.length !== 14) {
      return throwError(() => new Error('CNPJ inválido'));
    }

    const encoded = encodeURIComponent(cleanCnpj);
    const cnpjaUrl = `https://open.cnpja.com/office/${encoded}`;
    const brasilUrl = `https://brasilapi.com.br/api/cnpj/v1/${encoded}`;

    return this.http.get<unknown>(cnpjaUrl).pipe(
      map((raw) => this.normalizeCnpjaRaw(raw)),
      catchError(() =>
        this.http.get<BrasilApiCnpjResponse>(brasilUrl).pipe(
          map((d) => this.normalizeBrasilApiResponse(d)),
          map((data) => {
            if (this.isLookupEmpty(data)) {
              throw new Error('CNPJ lookup failed');
            }
            return data;
          })
        )
      )
    );
  }

  private isLookupEmpty(data: CnpjOfficeData): boolean {
    const hasName = !!(data.company?.name && String(data.company.name).trim());
    const a = data.address;
    const hasAddr = !!(
      a &&
      (
        (a.zip && String(a.zip).replace(/\D/g, '').length === 8) ||
        (a.street && String(a.street).trim()) ||
        (a.city && String(a.city).trim())
      )
    );
    const hasPhone = !!(data.phoneDigits && data.phoneDigits.length >= 10);
    const hasEmail = !!(data.email && String(data.email).trim());
    const hasProductHint = !!(
      data.suggestedProductDescription &&
      String(data.suggestedProductDescription).trim().length >= 10
    );
    return !hasName && !hasAddr && !hasPhone && !hasEmail && !hasProductHint;
  }

  private normalizeCnpjaRaw(raw: unknown): CnpjOfficeData {
    if (!raw || typeof raw !== 'object') {
      return {};
    }
    const r = raw as Record<string, unknown>;
    const company = r['company'] as Record<string, unknown> | undefined;
    const companyName =
      (typeof company?.['name'] === 'string' ? company['name'] : '') ||
      (typeof r['razao_social'] === 'string' ? (r['razao_social'] as string) : '');

    let alias = typeof r['alias'] === 'string' ? (r['alias'] as string) : '';
    if (!alias.trim() && typeof r['nome_fantasia'] === 'string') {
      alias = r['nome_fantasia'] as string;
    }

    const mainActivityText = this.extractMainActivityText(r, company);
    const responsibleName = this.extractResponsibleNameFromMembers(company);

    let phoneDigits: string | undefined;
    const phones = r['phones'];
    if (Array.isArray(phones) && phones.length > 0) {
      const p = phones[0] as Record<string, unknown>;
      const area = String(p['area'] ?? p['ddd'] ?? '').replace(/\D/g, '');
      const num = String(p['number'] ?? '').replace(/\D/g, '');
      const combined = `${area}${num}`.replace(/\D/g, '');
      if (combined.length >= 10 && combined.length <= 11) {
        phoneDigits = combined;
      }
    }

    let email: string | undefined;
    const emails = r['emails'];
    if (Array.isArray(emails) && emails.length > 0) {
      const e = emails[0] as Record<string, unknown>;
      const addr = e['address'];
      if (typeof addr === 'string' && addr.trim()) {
        email = addr.trim();
      }
    }

    const addrRaw = r['address'] as Record<string, unknown> | undefined;
    let address: CnpjOfficeData['address'] | undefined;
    if (addrRaw && typeof addrRaw === 'object') {
      const cityVal = addrRaw['city'];
      let cityStr = '';
      if (typeof cityVal === 'string') {
        cityStr = cityVal;
      } else if (cityVal && typeof cityVal === 'object' && 'name' in (cityVal as object)) {
        const n = (cityVal as Record<string, unknown>)['name'];
        cityStr = typeof n === 'string' ? n : '';
      }
      const zipRaw = addrRaw['zip'];
      const zipDigits =
        zipRaw != null ? String(zipRaw).replace(/\D/g, '') : '';

      address = {
        street: typeof addrRaw['street'] === 'string' ? (addrRaw['street'] as string) : '',
        number:
          addrRaw['number'] != null && addrRaw['number'] !== ''
            ? String(addrRaw['number'])
            : '',
        details: typeof addrRaw['details'] === 'string' ? (addrRaw['details'] as string) : '',
        district: typeof addrRaw['district'] === 'string' ? (addrRaw['district'] as string) : '',
        zip: zipDigits,
        state: typeof addrRaw['state'] === 'string' ? (addrRaw['state'] as string) : '',
        city: cityStr
      };
    }

    const suggestedProductDescription = this.extractProductHintFromCnpja(r, company);

    return {
      company: { name: companyName },
      alias: alias.trim() || companyName,
      responsibleName,
      mainActivityText,
      phoneDigits,
      email,
      address,
      suggestedProductDescription
    };
  }

  private extractMainActivityText(
    r: Record<string, unknown>,
    company: Record<string, unknown> | undefined,
  ): string | undefined {
    const mainActivity = r['mainActivity'] ?? company?.['mainActivity'];
    if (mainActivity && typeof mainActivity === 'object') {
      const m = mainActivity as Record<string, unknown>;
      const text =
        (typeof m['text'] === 'string' ? m['text'] : '') ||
        (typeof m['description'] === 'string' ? m['description'] : '');
      const t = text.trim();
      if (t.length > 0) {
        return t;
      }
    }
    return undefined;
  }

  private extractResponsibleNameFromMembers(
    company: Record<string, unknown> | undefined,
  ): string | undefined {
    const members = company?.['members'];
    if (!Array.isArray(members) || members.length === 0) {
      return undefined;
    }

    // Prefer "Sócio-Administrador" quando existir.
    const preferred = members.find((m) => {
      const roleText = (m as Record<string, unknown>)?.['role'];
      const text = roleText && typeof roleText === 'object'
        ? String((roleText as Record<string, unknown>)['text'] ?? '')
        : '';
      return text.toLowerCase().includes('sócio-administrador');
    }) as Record<string, unknown> | undefined;

    const pick = (preferred ?? (members[0] as Record<string, unknown> | undefined));
    const person = pick?.['person'];
    if (!person || typeof person !== 'object') {
      return undefined;
    }
    const name = (person as Record<string, unknown>)['name'];
    if (typeof name === 'string' && name.trim().length > 0) {
      return name.trim();
    }
    return undefined;
  }

  /** CNAE / atividade principal quando a CNPJa expõe no JSON. */
  private extractProductHintFromCnpja(
    r: Record<string, unknown>,
    company: Record<string, unknown> | undefined
  ): string | undefined {
    const mainActivity = r['mainActivity'] ?? company?.['mainActivity'];
    if (mainActivity && typeof mainActivity === 'object') {
      const m = mainActivity as Record<string, unknown>;
      const text =
        (typeof m['text'] === 'string' ? m['text'] : '') ||
        (typeof m['description'] === 'string' ? m['description'] : '');
      const t = text.trim();
      if (t.length >= 10) {
        return t;
      }
    }

    const activities = r['activities'];
    if (Array.isArray(activities) && activities.length > 0) {
      const a = activities[0] as Record<string, unknown>;
      const d =
        (typeof a['description'] === 'string' ? a['description'] : '') ||
        (typeof a['text'] === 'string' ? a['text'] : '');
      const s = d.trim();
      if (s.length >= 10) {
        return s;
      }
    }

    return undefined;
  }

  private normalizeBrasilApiResponse(data: BrasilApiCnpjResponse): CnpjOfficeData {
    const cepDigits = (data?.cep ?? '').replace(/\D/g, '');
    const phoneDigits =
      this.buildPhoneDigits(data?.ddd_telefone_1, data?.telefone_1) ||
      this.buildPhoneDigits(data?.ddd_telefone_2, data?.telefone_2);
    const emailRaw = data?.email;
    const email =
      typeof emailRaw === 'string' && emailRaw.trim() ? emailRaw.trim() : undefined;

    const cnae = (data?.cnae_fiscal_descricao ?? '').trim();
    let suggestedProductDescription: string | undefined;
    if (cnae.length >= 10) {
      suggestedProductDescription = cnae;
    } else if (cnae.length > 0 && (data?.razao_social ?? '').trim()) {
      const merged = `${cnae} — ${(data?.razao_social ?? '').trim()}`;
      if (merged.length >= 10) {
        suggestedProductDescription = merged;
      }
    }

    return {
      company: {
        name: data?.razao_social || ''
      },
      alias: data?.nome_fantasia || data?.razao_social || '',
      phoneDigits,
      email,
      suggestedProductDescription,
      address: {
        street: data?.logradouro || '',
        number: data?.numero || '',
        details: data?.complemento || '',
        district: data?.bairro || '',
        zip: cepDigits,
        state: data?.uf || '',
        city: data?.municipio || ''
      }
    };
  }

  private buildPhoneDigits(ddd?: string, phone?: string): string | undefined {
    const d = (ddd ?? '').replace(/\D/g, '');
    const n = (phone ?? '').replace(/\D/g, '');
    const combined = `${d}${n}`.replace(/\D/g, '');
    if (combined.length >= 10 && combined.length <= 11) {
      return combined;
    }
    return undefined;
  }
}
